import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and numeric-vs-string live in the
// library, not in a per-module copy.
import {useTableSort} from '@flexinet/ui-components';

import {fetchRecords} from '../services/ProductLineItemsService';
import {ProductLineItem} from '../types';
import {productLineItemsQueryKeys} from './productLineItemsQueryKeys';

// Page size is a server concern fixed by the module (§9), never a user choice.
const PAGE_SIZE = 10;

// The order REQUESTED from the API. The resolver honours `sort`: `created` reads
// record-type-created-index; `position` reads product-position-index, which is sparse on a
// nullable column and would drop line items saved without a position. So the request stays
// on `created`, and every column sorts client-side over the loaded rows.
const SERVER_SORT = {column: 'created', direction: 'desc'} as const;

// NEWEST FIRST, on `created`: the same order the server returns, so first paint agrees
// with it. `created` is nullable in the schema (AWSDateTime), but every listed row carries
// it: a row without it would not be in record-type-created-index at all.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<ProductLineItem> = {
    columnAccessor: 'created',
    direction: 'desc',
};

const matchesKeyword = (item: ProductLineItem, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.productFeature?.name,
        item.productFeatureId,
        typeof item.position === 'number' ? String(item.position) : undefined,
        item.id,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useProductLineItems = (productId: string) => {
    if (!productId) {
        throw new Error('productId is required parameter');
    }

    const queryClient = useQueryClient();

    // Component state, not a module-scoped store. The search input (TableSearchForm) and
    // the table that reads the keyword are the SAME component, RecordsTable, so there is
    // no sibling for a store to reach. ProductComponent (the product preview) reads
    // `items`, the loaded set, and must not inherit a search.
    const [keyword, setKeyword] = useState('');

    // Only what the server receives is in the key. The keyword is applied client-side
    // below, so keying on it would refetch identical pages per keystroke.
    const query = useInfiniteQuery({
        queryKey: productLineItemsQueryKeys.list(productId),
        queryFn: ({pageParam}: { pageParam: string | null }) => fetchRecords({
            productId,
            limit: PAGE_SIZE,
            sort: SERVER_SORT,
            nextToken: pageParam,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        // Line items change only through this module, and every mutation invalidates
        // ['product-line-items'], so 30s stops a tab switch from refetching without hiding
        // an edit.
        staleTime: 30_000,
    });

    const items: ProductLineItem[] =
        query.data?.pages?.flatMap(page => (page && 'items' in page && page.items) ? page.items : []) ?? [];

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token
    // pagination the browser cannot order or search rows it has not loaded; that is a
    // property of the model, not a bug (§9). Sort state is component-level: useTableSort
    // holds it.
    const {sortStatus, setSortStatus, sorted} = useTableSort<ProductLineItem>(
        items.filter(item => matchesKeyword(item, needle)),
        DEFAULT_SORT_STATUS,
    );

    const lastPage = query.data?.pages?.[query.data.pages.length - 1];

    return {
        // The loaded set BEFORE keyword filtering: what the product preview lists, and
        // what the footer's "N of M loaded" is measured against.
        items,
        visibleItems: sorted,
        keyword,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        // The server's own cursor, never a stand-in: opaque to this client (§9).
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        // Loads the NEXT page. Guarded on isFetchingNextPage too, so scroll-to-bottom and
        // Load More cannot start overlapping requests for the same cursor.
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        // Reload starts again from page 1, for every list of every product.
        reloadRecords: () => queryClient.invalidateQueries({queryKey: productLineItemsQueryKeys.all}),
        handleFilterByKeyword: (newKeyword: string) => setKeyword(newKeyword),
    };
};
